import classNames from 'classnames';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { AspectRatioWrapper, Button } from '../../components';
import VideoPlaceholder from './components/VideoPlaceholder';

import css from './ProductImageSlider.module.css';

export default function ProductImageSlider({ images, imageAlt, onReady }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState('left');
  const [touchStart, setTouchStart] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const sliderRef = useRef(null);
  const thumbnailRailRef = useRef(null);
  const hasThumbnailRail = images.length > 1;
  const isVideo = images[currentImageIndex].includes('amazonaws');
  const imageIndexes = images.reduce((indexes, image, index) => {
    if (!image.includes('amazonaws')) {
      indexes.push(index);
    }
    return indexes;
  }, []);
  const lightboxSlides = imageIndexes.map(index => ({ src: images[index] }));
  const currentLightboxIndex = imageIndexes.indexOf(currentImageIndex);

  const navigateToIndex = nextIndex => {
    if (nextIndex === currentImageIndex) {
      return;
    }

    const movingForward = nextIndex > currentImageIndex;
    setSlideDirection(movingForward ? 'left' : 'right');
    setCurrentImageIndex(nextIndex);
  };

  const goToNext = () => {
    const nextIndex = (currentImageIndex + 1) % images.length;
    setSlideDirection('left');
    setCurrentImageIndex(nextIndex);
  };

  const goToPrev = () => {
    const prevIndex = (currentImageIndex - 1 + images.length) % images.length;
    setSlideDirection('right');
    setCurrentImageIndex(prevIndex);
  };

  // Handle swipe gestures for mobile/tablet
  const handleTouchStart = e => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleSwipe = touchEndPosition => {
    const distance = touchStart - touchEndPosition;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrev();
    }
  };

  const handleTouchEnd = e => {
    handleSwipe(e.changedTouches[0].clientX);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        goToPrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [images.length, currentImageIndex]);

  useEffect(() => {
    if (typeof onReady !== 'function') {
      return undefined;
    }

    if (!Array.isArray(images) || images.length === 0) {
      onReady();
      return undefined;
    }

    let isCancelled = false;

    const preloadMedia = async () => {
      const preloadTasks = images.map(image => {
        if (!image || image.includes('amazonaws')) {
          return Promise.resolve();
        }

        return new Promise(resolve => {
          const preloadImage = new window.Image();

          preloadImage.onload = () => resolve();
          preloadImage.onerror = () => resolve();
          preloadImage.src = image;
        });
      });

      await Promise.all(preloadTasks);

      if (!isCancelled) {
        onReady();
      }
    };

    preloadMedia();

    return () => {
      isCancelled = true;
    };
  }, [images, onReady]);

  useEffect(() => {
    if (typeof window === 'undefined' || !hasThumbnailRail) {
      return undefined;
    }

    const sliderElement = sliderRef.current;
    const thumbnailRailElement = thumbnailRailRef.current;

    if (!sliderElement || !thumbnailRailElement) {
      return undefined;
    }

    const desktopMediaQuery = window.matchMedia('(min-width: 768px)');

    const updateThumbnailRailHeight = () => {
      if (!desktopMediaQuery.matches) {
        thumbnailRailElement.style.removeProperty('--thumbnailRailMaxHeight');
        return;
      }

      const nextHeight = Math.round(sliderElement.getBoundingClientRect().height);

      if (nextHeight > 0) {
        thumbnailRailElement.style.setProperty('--thumbnailRailMaxHeight', `${nextHeight}px`);
      }
    };

    updateThumbnailRailHeight();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateThumbnailRailHeight) : null;

    if (resizeObserver) {
      resizeObserver.observe(sliderElement);
    }

    window.addEventListener('resize', updateThumbnailRailHeight);

    if (typeof desktopMediaQuery.addEventListener === 'function') {
      desktopMediaQuery.addEventListener('change', updateThumbnailRailHeight);
    } else if (typeof desktopMediaQuery.addListener === 'function') {
      desktopMediaQuery.addListener(updateThumbnailRailHeight);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      window.removeEventListener('resize', updateThumbnailRailHeight);

      if (typeof desktopMediaQuery.removeEventListener === 'function') {
        desktopMediaQuery.removeEventListener('change', updateThumbnailRailHeight);
      } else if (typeof desktopMediaQuery.removeListener === 'function') {
        desktopMediaQuery.removeListener(updateThumbnailRailHeight);
      }
    };
  }, [currentImageIndex, hasThumbnailRail, images.length]);

  const openLightbox = () => {
    if (!isVideo && currentLightboxIndex >= 0) {
      setIsLightboxOpen(true);
    }
  };

  return (
    <>
      <div className={css.container}>
        {/* Image Slider Section */}
        <div
          className={classNames(css.sliderSection, {
            [css.sliderSectionWithThumbnails]: hasThumbnailRail,
          })}
        >
          {/* Main Slider - Horizontal on Mobile, Vertical on Desktop */}
          <div
            className={css.mainSlider}
            ref={sliderRef}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            role="region"
            aria-label="Product image slider"
          >
            {/* Image Container with Absolute Positioned Arrows */}
            <div
              key={`${currentImageIndex}-${slideDirection}`}
              className={classNames(css.slideFrame, {
                [css.slideFromRight]: slideDirection === 'left',
                [css.slideFromLeft]: slideDirection === 'right',
              })}
            >
              {isVideo ? (
                <AspectRatioWrapper width={1} height={1}>
                  <video alt="video" className={css.videoContainer} autoPlay>
                    <source src={images[currentImageIndex]} type={'video/mp4'} />
                    Your browser does not support the video tag.
                  </video>
                </AspectRatioWrapper>
              ) : (
                <button
                  type="button"
                  className={css.mainImageButton}
                  onClick={openLightbox}
                  aria-label={`Open product image ${currentImageIndex + 1} of ${images.length}`}
                >
                  <img
                    src={images[currentImageIndex]}
                    alt={`${imageAlt} - Image ${currentImageIndex + 1}`}
                    className={css.mainImage}
                  />
                </button>
              )}
            </div>

            {/* Left Arrow - Positioned inside image */}
            {images.length > 1 && (
              <Button
                onClick={goToPrev}
                disabled={currentImageIndex === 0}
                className={classNames(
                  css.arrowButton,
                  css.arrowLeft,
                  currentImageIndex === 0 && css.disabled
                )}
                aria-label="Previous image"
              >
                <ChevronLeft className={css.arrowIcon} />
              </Button>
            )}

            {/* Right Arrow - Positioned inside image */}
            {images.length > 1 && (
              <Button
                onClick={goToNext}
                disabled={currentImageIndex === images.length - 1}
                className={classNames(
                  css.arrowButton,
                  css.arrowRight,
                  currentImageIndex === images.length - 1 && css.disabled
                )}
                aria-label="Next image"
              >
                <ChevronRight className={css.arrowIcon} />
              </Button>
            )}
          </div>

          {/* Mobile Swipe Indicators */}
          <div className={css.swipeIndicators}>
            {images.map((_, index) => (
              <span
                key={index}
                className={`${css.indicator} ${
                  index === currentImageIndex ? css.activeIndicator : ''
                }`}
                aria-hidden="true"
              />
            ))}
          </div>

          {/* Thumbnail Navigation - show under image on mobile and desktop */}
          {hasThumbnailRail && (
            <div className={css.thumbnailContainer} ref={thumbnailRailRef}>
              {images.map((image, index) => (
                <button
                  key={index}
                  className={`${css.thumbnail} ${index === currentImageIndex ? css.active : ''}`}
                  onClick={() => navigateToIndex(index)}
                  aria-label={`View image ${index + 1}`}
                  aria-current={index === currentImageIndex}
                >
                  {image.includes('amazonaws') ? (
                    <VideoPlaceholder />
                  ) : (
                    <img src={image} alt={`Thumbnail ${index + 1}`} />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {lightboxSlides.length > 0 ? (
        <Lightbox
          open={isLightboxOpen}
          close={() => setIsLightboxOpen(false)}
          index={currentLightboxIndex >= 0 ? currentLightboxIndex : 0}
          slides={lightboxSlides}
          plugins={[Zoom]}
          zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
          controller={{ closeOnBackdropClick: true }}
          carousel={{ finite: lightboxSlides.length <= 1 }}
          on={{
            view: ({ index }) => {
              const nextImageIndex = imageIndexes[index];
              if (typeof nextImageIndex === 'number') {
                setCurrentImageIndex(nextImageIndex);
              }
            },
          }}
        />
      ) : null}
    </>
  );
}
